/**
 * Dashboard Skeleton Loaders
 * Feature: 101-codebase-audit-refactor (T105)
 *
 * Skeleton components for improved perceived performance while loading dashboard data.
 * Provides visual feedback during data fetching to reduce perceived load time.
 */

import React from 'react';

// =============================================================================
// Base Skeleton Component
// =============================================================================

interface SkeletonProps {
  className?: string;
  animate?: boolean;
  style?: React.CSSProperties;
}

export const Skeleton: React.FC<SkeletonProps> = ({ className = '', animate = true, style }) => (
  <div
    className={`bg-[var(--color-bg-subtle)] rounded ${animate ? 'animate-pulse' : ''} ${className}`}
    style={style}
  />
);

// =============================================================================
// Stat Card Skeleton
// =============================================================================

export const StatCardSkeleton: React.FC = () => (
  <div className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4">
    <Skeleton className="h-4 w-20 mb-2" />
    <Skeleton className="h-8 w-16 mb-2" />
    <Skeleton className="h-3 w-24" />
  </div>
);

// =============================================================================
// Widget Skeleton
// =============================================================================

interface WidgetSkeletonProps {
  rows?: number;
}

export const WidgetSkeleton: React.FC<WidgetSkeletonProps> = ({ rows = 4 }) => (
  <div className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4">
    <Skeleton className="h-5 w-32 mb-4" />
    <div className="space-y-3">
      {Array.from({ length: rows }).map((_, i) => (
        <div key={i} className="flex items-center justify-between">
          <div className="flex items-center space-x-3">
            <Skeleton className="h-8 w-8 rounded-full" />
            <div>
              <Skeleton className="h-4 w-40 mb-1" />
              <Skeleton className="h-3 w-24" />
            </div>
          </div>
          <Skeleton className="h-6 w-16 rounded-full" />
        </div>
      ))}
    </div>
  </div>
);

// =============================================================================
// Chart Skeleton
// =============================================================================

export const ChartSkeleton: React.FC = () => (
  <div className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] p-4">
    <Skeleton className="h-5 w-40 mb-4" />
    <div className="flex items-end justify-between h-40 space-x-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton
          key={i}
          className="flex-1"
          style={{ height: `${30 + Math.random() * 70}%` }}
        />
      ))}
    </div>
    <div className="flex justify-between mt-2">
      {Array.from({ length: 7 }).map((_, i) => (
        <Skeleton key={i} className="h-3 w-8" />
      ))}
    </div>
  </div>
);

// =============================================================================
// Table Skeleton
// =============================================================================

interface TableSkeletonProps {
  rows?: number;
  columns?: number;
}

export const TableSkeleton: React.FC<TableSkeletonProps> = ({ rows = 5, columns = 4 }) => (
  <div className="bg-[var(--color-bg-card)] rounded-lg border border-[var(--color-border)] overflow-hidden">
    {/* Header */}
    <div className="bg-[var(--color-bg-subtle)] px-4 py-3 border-b border-[var(--color-border)]">
      <div className="flex space-x-4">
        {Array.from({ length: columns }).map((_, i) => (
          <Skeleton key={i} className="h-4 flex-1" />
        ))}
      </div>
    </div>
    {/* Rows */}
    <div className="divide-y divide-[var(--color-border)]">
      {Array.from({ length: rows }).map((_, rowIndex) => (
        <div key={rowIndex} className="px-4 py-3 flex space-x-4">
          {Array.from({ length: columns }).map((_, colIndex) => (
            <Skeleton key={colIndex} className="h-4 flex-1" />
          ))}
        </div>
      ))}
    </div>
  </div>
);

// =============================================================================
// Full Dashboard Skeleton
// =============================================================================

export const DashboardSkeleton: React.FC = () => (
  <div className="min-h-screen bg-[var(--color-bg-subtle)] p-6">
    {/* Header */}
    <div className="mb-6">
      <Skeleton className="h-8 w-48 mb-2" />
      <Skeleton className="h-4 w-64" />
    </div>

    {/* Stats Grid */}
    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
      <StatCardSkeleton />
      <StatCardSkeleton />
      <StatCardSkeleton />
      <StatCardSkeleton />
    </div>

    {/* Content Grid */}
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
      <div className="lg:col-span-2">
        <WidgetSkeleton rows={5} />
      </div>
      <div className="lg:col-span-1">
        <WidgetSkeleton rows={4} />
      </div>
      <div className="lg:col-span-2">
        <ChartSkeleton />
      </div>
      <div className="lg:col-span-1">
        <WidgetSkeleton rows={3} />
      </div>
    </div>
  </div>
);

export default DashboardSkeleton;
